import React, { useCallback, useMemo, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { RequestedVideo, useFetchRequestedVideosQuery } from "@/api/requestedVideos";
import { Loading } from "@/common/containers/Loading";
import { useDispatch } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";

import PreviewModal from "./PreviewModal/PreviewModal";
import useStyles from "./RequestedVideosPage.styles";
import { MemoizedRequestedVideosTable } from "./RequestedVideosTable/RequestedVideosTable";


export interface RequestedVideosPageQueryParams {
    videoId?: number;
    videoType?: VideoType;
}
export const VIDEO_ID_PARAM = "video_id";
export const VIDEO_TYPE_PARAM = "video_type";

export enum VideoType {
    Dewarped = "dewarped",
}

const manipulateVideos = (videos: RequestedVideo[]): RequestedVideo[] => {
    const regularAndDewarpedVideoList: RequestedVideo[] = [];

    videos.forEach((entry) => {
        const regularVideo: RequestedVideo = JSON.parse(JSON.stringify(entry));

        regularVideo.dewarpedVideoUrl = undefined;
        regularAndDewarpedVideoList.push(regularVideo);

        if (entry.dewarpedVideoUrl) {
            const dewarpedVideo = JSON.parse(JSON.stringify(entry));

            dewarpedVideo.videoUrl = undefined;
            regularAndDewarpedVideoList.push(dewarpedVideo);
        }
    });

    regularAndDewarpedVideoList.sort((a: RequestedVideo, b: RequestedVideo) => {
        const aDateMs = new Date(a.recordedDatetime || 0).getTime();
        const bDateMs = new Date(b.recordedDatetime || 0).getTime();

        return bDateMs - aDateMs;
    });

    return regularAndDewarpedVideoList;
};

interface RequestedVideosLiveUnitProps {
    liveUnitId: number;
}

// add archive cabinet props when it is supported
export type RequestedVideosProps = RequestedVideosLiveUnitProps;

export const RequestedVideosPage: React.FC<RequestedVideosProps> = ({ liveUnitId }) => {
    const { data: preprocessedVideos = [], error, isLoading } = useFetchRequestedVideosQuery({ liveUnitId });
    const videos = useMemo(() => manipulateVideos(preprocessedVideos), [preprocessedVideos]);

    const styles = useStyles();
    const dispatch = useDispatch();
    const { search, pathname } = useLocation();
    const history = useHistory();

    const setVideoParams = (video: RequestedVideo | null) => {
        const searchParams = new URLSearchParams(search);

        if (video == null) {
            searchParams.delete(VIDEO_ID_PARAM);
            searchParams.delete(VIDEO_TYPE_PARAM);
        } else {
            searchParams.set(VIDEO_ID_PARAM, video.id.toString());

            if (video.dewarpedVideoUrl != null) {
                searchParams.set(VIDEO_TYPE_PARAM, VideoType.Dewarped);
            }
        }

        history.replace({
            pathname,
            search: searchParams.toString(),
        });
    };

    useEffect(() => {
        if (error) {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `Failed to retrieve requested videos: ${error}`,
                }),
            );
        }
    }, [error]);

    const selectedVideo = useMemo(() => {
        const searchParams = new URLSearchParams(search);
        const videoId = parseInt(searchParams.get(VIDEO_ID_PARAM) as string, 10);
        const videoType = searchParams.get(VIDEO_TYPE_PARAM);

        if (!isNaN(videoId)) {
            const video = videos
                .filter((v) =>
                    videoType == VideoType.Dewarped ? v.dewarpedVideoUrl != null : v.dewarpedVideoUrl == null,
                )
                .find((v) => v.id == videoId);

            if (video) {
                return video;
            }
        }

        return null;
    }, [search, videos]);

    const handleClose = useCallback(() => setVideoParams(null), []);
    const handleSelected = useCallback((_: React.MouseEvent, video: RequestedVideo) => setVideoParams(video), []);

    return (
        <div className={styles.root}>
            <div className={styles.tablePrefix}>
                {/* <span className={styles.title}>{t("previouslyRequestedVideos")}</span> */}
                <span className={styles.title}>Previously Requested Videos</span>
                {videos.length > 0 && (
                    <span className={styles.location}>
                        {videos[0].locationStreet && `${videos[0].locationStreet}, `}
                        {videos[0].locationCity && `${videos[0].locationCity}, `}
                        {videos[0].locationState && `${videos[0].locationState}, `}
                        {videos[0].locationZip}
                    </span>
                )}
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                <MemoizedRequestedVideosTable
                    videos={videos}
                    handleVideoRowClicked={handleSelected}
                    liveUnitId={liveUnitId}
                />
            )}

            {selectedVideo !== null && (
                <PreviewModal open={selectedVideo !== null} onClose={handleClose} video={selectedVideo} />
            )}
        </div>
    );
};
